import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config/api';
import { useDashboardStore } from '../store/dashboardStore';

export const SimpleAnalytics: React.FC = () => {
    const [data, setData] = useState<any>(null);
    const [bySheet, setBySheet] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const { filters } = useDashboardStore();

    useEffect(() => {
        fetchData();

        // Listen for refresh events
        const handleRefresh = () => {
            fetchData();
        };

        window.addEventListener('refreshAnalytics', handleRefresh);

        return () => {
            window.removeEventListener('refreshAnalytics', handleRefresh);
        };
        // Re-fetch when global filters change so all tabs stay in sync
    }, [filters?.quarter, (filters as any)?.datasetTag]);

    const fetchData = async () => {
        try {
            setLoading(true);
            console.log('🔍 Fetching analytics...');

            // Add cache-busting to ensure fresh analytics after uploads
            const params = new URLSearchParams();
            if (filters?.quarter) params.set('quarter', filters.quarter);
            if ((filters as any)?.datasetTag) params.set('datasetTag', (filters as any).datasetTag as string);
            params.set('ts', String(Date.now()));
            const url = buildApiUrl(`/api/analytics/simple?${params.toString()}`);
            const response = await fetch(url, {
                method: 'GET',
                cache: 'no-store'
            });
            console.log('📡 Response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const result = await response.json();
            console.log('📊 Data received:', result);
            console.log('📊 Redeemed count:', result.redeemedCount);
            console.log('📊 Assigned count:', result.assignedCount);
            console.log('📊 Voucher values:', result.voucherValues);

            if (result.success) {
                setData(result);
                setError(null);
            } else {
                setError(result.error || 'Failed to fetch data');
            }

            // Fetch per-sheet summary
            const bySheetParams = new URLSearchParams();
            if (filters?.quarter) bySheetParams.set('quarter', filters.quarter);
            if ((filters as any)?.datasetTag) bySheetParams.set('datasetTag', (filters as any).datasetTag as string);
            bySheetParams.set('ts', String(Date.now()));
            const bySheetRes = await fetch(buildApiUrl(`/api/analytics/by-sheet?${bySheetParams.toString()}`), { method: 'GET', cache: 'no-store' });
            if (bySheetRes.ok) {
                const bySheetJson = await bySheetRes.json();
                if (bySheetJson.success) setBySheet(bySheetJson.summary);
            }
        } catch (err) {
            console.error('❌ Fetch error:', err);
            setError(err instanceof Error ? err.message : 'Unknown error');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <div className="animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="space-y-3">
                        <div className="h-3 bg-gray-200 rounded"></div>
                        <div className="h-3 bg-gray-200 rounded w-5/6"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-800 font-medium mb-2">Error Loading Analytics</h3>
                <p className="text-red-600 text-sm mb-4">{error}</p>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
                <p className="text-gray-500">No analytics data available</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {bySheet && (
                <div className="bg-white rounded-lg shadow p-6">
                    <h3 className="text-lg font-semibold mb-4">Per-Sheet Redemption</h3>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="p-4 rounded-lg bg-blue-50">
                            <div className="text-sm text-blue-700 font-medium">CLF (Foundational)</div>
                            <div className="text-2xl font-bold text-blue-900 mt-1">{bySheet.bySheet.foundational.total}</div>
                            <div className="text-sm text-blue-700">Redeemed: {bySheet.bySheet.foundational.redeemed} ({bySheet.bySheet.foundational.rate}%)</div>
                        </div>
                        <div className="p-4 rounded-lg bg-purple-50">
                            <div className="text-sm text-purple-700 font-medium">DVA (Associate)</div>
                            <div className="text-2xl font-bold text-purple-900 mt-1">{bySheet.bySheet.associate.total}</div>
                            <div className="text-sm text-purple-700">Redeemed: {bySheet.bySheet.associate.redeemed} ({bySheet.bySheet.associate.rate}%)</div>
                        </div>
                        {bySheet.bySheet.aif?.total > 0 && (
                            <div className="p-4 rounded-lg bg-green-50">
                                <div className="text-sm text-green-700 font-medium">AIF</div>
                                <div className="text-2xl font-bold text-green-900 mt-1">{bySheet.bySheet.aif.total}</div>
                                <div className="text-sm text-green-700">Redeemed: {bySheet.bySheet.aif.redeemed} ({bySheet.bySheet.aif.rate}%)</div>
                            </div>
                        )}
                        <div className="p-4 rounded-lg bg-gray-50">
                            <div className="text-sm text-gray-700 font-medium">Overall</div>
                            <div className="text-2xl font-bold text-gray-900 mt-1">{bySheet.totals.total}</div>
                            <div className="text-sm text-gray-700">Redeemed: {bySheet.totals.redeemed} ({bySheet.totals.rate}%)</div>
                        </div>
                    </div>
                </div>
            )}
            {/* Header */}
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">AWS Voucher Analytics</h2>
                <p className="text-gray-600">Complete breakdown of your voucher program</p>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-500">Total Vouchers</div>
                    <div className="text-3xl font-bold text-blue-600">{data.totalVouchers}</div>
                    <div className="text-sm text-gray-600 mt-1">
                        ${data.voucherValues?.totalValue?.toLocaleString()} total value
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-500">CLF Vouchers</div>
                    <div className="text-3xl font-bold text-green-600">{data.voucherValues?.foundationalCount}</div>
                    <div className="text-sm text-gray-600 mt-1">
                        ${data.voucherValues?.foundationalValue?.toLocaleString()} value
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-500">DVA Vouchers</div>
                    <div className="text-3xl font-bold text-purple-600">{data.voucherValues?.associateCount}</div>
                    <div className="text-sm text-gray-600 mt-1">
                        ${data.voucherValues?.associateValue?.toLocaleString()} value
                    </div>
                </div>

                {(data.voucherValues?.aifCount || 0) > 0 && (
                    <div className="bg-white rounded-lg shadow p-6">
                        <div className="text-sm font-medium text-gray-500">AIF Vouchers</div>
                        <div className="text-3xl font-bold text-indigo-600">{data.voucherValues?.aifCount}</div>
                        <div className="text-sm text-gray-600 mt-1">
                            ${(data.voucherValues?.aifValue || 0).toLocaleString()} value
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-500">Redemption Rate</div>
                    <div className="text-3xl font-bold text-orange-600">
                        {data.redeemedCount && data.totalVouchers ?
                            `${((data.redeemedCount / data.totalVouchers) * 100).toFixed(1)}%` :
                            'N/A'}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                        {data.redeemedCount || 0} of {data.totalVouchers} redeemed
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow p-6">
                    <div className="text-sm font-medium text-gray-500">Assigned</div>
                    <div className="text-3xl font-bold text-red-600">{data.assignedCount || 'N/A'}</div>
                    <div className="text-sm text-gray-600 mt-1">
                        {data.assignedCount && data.totalVouchers ?
                            `${((data.assignedCount / data.totalVouchers) * 100).toFixed(1)}% of total` :
                            'Loading...'}
                    </div>
                </div>
            </div>

            {/* Visual Charts */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Visual Analytics</h3>

                {/* Redemption Status Chart */}
                <div className="mb-6">
                    <h4 className="font-medium mb-3 text-gray-700">Redemption Status</h4>
                    <div className="space-y-3">
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Redeemed ({data.redeemedCount || 0})</span>
                                <span className="font-medium text-green-600">
                                    {data.redeemedCount && data.totalVouchers ?
                                        ((data.redeemedCount / data.totalVouchers) * 100).toFixed(1) : '0'}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                    className="bg-green-500 h-4 rounded-full transition-all duration-500"
                                    style={{
                                        width: `${data.redeemedCount && data.totalVouchers ?
                                            (data.redeemedCount / data.totalVouchers) * 100 : 0}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">Assigned ({data.assignedCount || 0})</span>
                                <span className="font-medium text-blue-600">
                                    {data.assignedCount && data.totalVouchers ?
                                        ((data.assignedCount / data.totalVouchers) * 100).toFixed(1) : '0'}%
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                    className="bg-blue-500 h-4 rounded-full transition-all duration-500"
                                    style={{
                                        width: `${data.assignedCount && data.totalVouchers ?
                                            (data.assignedCount / data.totalVouchers) * 100 : 0}%`
                                    }}
                                ></div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Voucher Type Chart */}
                <div>
                    <h4 className="font-medium mb-3 text-gray-700">Voucher Types</h4>
                    <div className="space-y-3">
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">CLF (Foundational) - $75 each</span>
                                <span className="font-medium text-blue-600">
                                    {data.voucherValues?.foundationalCount} vouchers
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                    className="bg-blue-600 h-4 rounded-full transition-all duration-500"
                                    style={{ width: `${data.voucherValues?.foundationalPercentage || 0}%` }}
                                ></div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                ${data.voucherValues?.foundationalValue?.toLocaleString()} ({data.voucherValues?.foundationalPercentage}%)
                            </div>
                        </div>
                        <div>
                            <div className="flex justify-between text-sm mb-1">
                                <span className="text-gray-600">DVA (Associate) - $150 each</span>
                                <span className="font-medium text-purple-600">
                                    {data.voucherValues?.associateCount} vouchers
                                </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div
                                    className="bg-purple-600 h-4 rounded-full transition-all duration-500"
                                    style={{ width: `${data.voucherValues?.associatePercentage || 0}%` }}
                                ></div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                ${data.voucherValues?.associateValue?.toLocaleString()} ({data.voucherValues?.associatePercentage}%)
                            </div>
                        </div>
                        {(data.voucherValues?.aifCount || 0) > 0 && (
                            <div>
                                <div className="flex justify-between text-sm mb-1">
                                    <span className="text-gray-600">AIF - $75 each</span>
                                    <span className="font-medium text-indigo-600">
                                        {data.voucherValues?.aifCount} vouchers
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-4">
                                    <div
                                        className="bg-indigo-600 h-4 rounded-full transition-all duration-500"
                                        style={{ width: `${data.voucherValues?.aifPercentage || 0}%` }}
                                    ></div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    ${(data.voucherValues?.aifValue || 0).toLocaleString()} ({data.voucherValues?.aifPercentage || 0}%)
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Voucher Value Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* CLF */}
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="text-blue-700 font-medium">CLF (Foundational)</div>
                        <div className="text-2xl font-bold text-blue-900">
                            {data.voucherValues?.foundationalCount}
                        </div>
                        <div className="text-blue-600">
                            ${data.voucherValues?.foundationalValue?.toLocaleString()}
                        </div>
                        <div className="text-blue-500 text-sm">
                            {data.voucherValues?.foundationalPercentage}% of total
                        </div>
                        <div className="mt-2 bg-blue-200 rounded-full h-2">
                            <div
                                className="bg-blue-600 h-2 rounded-full"
                                style={{ width: `${data.voucherValues?.foundationalPercentage}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* DVA */}
                    <div className="bg-green-50 p-4 rounded-lg">
                        <div className="text-green-700 font-medium">DVA (Associate)</div>
                        <div className="text-2xl font-bold text-green-900">
                            {data.voucherValues?.associateCount}
                        </div>
                        <div className="text-green-600">
                            ${data.voucherValues?.associateValue?.toLocaleString()}
                        </div>
                        <div className="text-green-500 text-sm">
                            {data.voucherValues?.associatePercentage}% of total
                        </div>
                        <div className="mt-2 bg-green-200 rounded-full h-2">
                            <div
                                className="bg-green-600 h-2 rounded-full"
                                style={{ width: `${data.voucherValues?.associatePercentage}%` }}
                            ></div>
                        </div>
                    </div>

                    {/* AIF */}
                    {(data.voucherValues?.aifCount || 0) > 0 && (
                        <div className="bg-purple-50 p-4 rounded-lg">
                            <div className="text-purple-700 font-medium">AIF</div>
                            <div className="text-2xl font-bold text-purple-900">
                                {data.voucherValues?.aifCount}
                            </div>
                            <div className="text-purple-600">
                                ${(data.voucherValues?.aifValue || 0).toLocaleString()}
                            </div>
                            <div className="text-purple-500 text-sm">
                                {(data.voucherValues?.aifPercentage || 0)}% of total
                            </div>
                            <div className="mt-2 bg-purple-200 rounded-full h-2">
                                <div
                                    className="bg-purple-600 h-2 rounded-full"
                                    style={{ width: `${data.voucherValues?.aifPercentage || 0}%` }}
                                ></div>
                            </div>
                        </div>
                    )}

                    {/* Total */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="text-gray-700 font-medium">Total Program Value</div>
                        <div className="text-2xl font-bold text-gray-900">
                            ${data.voucherValues?.totalValue?.toLocaleString()}
                        </div>
                        <div className="text-gray-600">
                            {data.totalVouchers} total vouchers
                        </div>
                        <div className="text-gray-500 text-sm">
                            CLF: $75 each • DVA: $150 each
                        </div>
                    </div>
                </div>
            </div>

            {/* Sheet Breakdown */}
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Voucher Type Breakdown</h3>
                <div className="space-y-4">
                    {data.sheetBreakdown?.map((sheet: any) => (
                        <div key={sheet.sheet_source} className="border-l-4 border-blue-500 pl-4">
                            <div className="flex justify-between items-center">
                                <div>
                                    <div className="font-medium text-gray-900">
                                        {sheet.sheet_source === 'foundational' ? 'CLF (Foundational)' : sheet.sheet_source === 'associate' ? 'DVA (Associate)' : 'AIF'}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        {sheet.count} vouchers
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold text-green-600">
                                        ${(sheet.count * (sheet.sheet_source === 'foundational' ? 75 : 150)).toLocaleString()}
                                    </div>
                                    <div className="text-sm text-gray-500">total value</div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Debug Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-medium text-yellow-800 mb-2">Debug Info</h4>
                <div className="text-xs text-yellow-700 space-y-1">
                    <div>Redeemed Count: {data.redeemedCount}</div>
                    <div>Assigned Count: {data.assignedCount}</div>
                    <div>Total Vouchers: {data.totalVouchers}</div>
                    <div>Foundational %: {data.voucherValues?.foundationalPercentage}</div>
                    <div>Associate %: {data.voucherValues?.associatePercentage}</div>
                </div>
            </div>

            {/* Refresh Button */}
            <div className="text-center">
                <button
                    onClick={fetchData}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    Refresh Data
                </button>
            </div>
        </div>
    );
};